import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, schoolApi } from '../../api/client';
import { FaLock, FaSchool, FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Spinner from '../../components/common/Spinner';

export default function ResetPassword() {
  const { slug }          = useParams();
  const navigate          = useNavigate();
  const [searchParams]    = useSearchParams();
  const token             = searchParams.get('token') || '';
  const emailParam        = searchParams.get('email') || '';

  const [school,    setSchool]   = useState(null);
  const [password,  setPassword] = useState('');
  const [confirm,   setConfirm]  = useState('');
  const [showPass,  setShowPass] = useState(false);
  const [loading,   setLoading]  = useState(false);
  const [fetching,  setFetching] = useState(!!slug);
  const [done,      setDone]     = useState(false);
  const isSuperAdmin = !slug;

  useEffect(() => {
    if (!slug) { setFetching(false); return; }
    schoolApi.getInfo(slug)
      .then(r => setSchool(r.data.data))
      .catch(() => navigate('/'))
      .finally(() => setFetching(false));
  }, [slug, navigate]);

  const primary = school?.primary_color || '#0d6efd';

  const strength = () => {
    let s = 0;
    if (password.length >= 8)          s++;
    if (/[A-Z]/.test(password))        s++;
    if (/[0-9]/.test(password))        s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  };

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', '#ef4444', '#f59e0b', '#0d6efd', '#10b981'];
  const str = strength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error('Passwords do not match');
    if (password.length < 8)  return toast.error('Password must be at least 8 characters');
    if (!token)               return toast.error('Invalid reset link');

    setLoading(true);
    try {
      await authApi.resetPassword({ token, email: emailParam, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally { setLoading(false); }
  };

  if (fetching) return <div className="login-page"><Spinner /></div>;

  if (!token || !emailParam) return (
    <div className="login-page">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>⚠️</div>
        <h3 style={{ color: '#ef4444', marginBottom: 8 }}>Invalid Reset Link</h3>
        <p style={{ color: '#6b7280', marginBottom: 20 }}>This link is missing required parameters. Please request a new one.</p>
        <button onClick={() => slug ? navigate(`/school/${slug}/forgot-password`) : navigate('/super-admin/forgot-password')}
          className="sms-btn sms-btn-primary" style={{ background: primary }}>
          Request New Link
        </button>
      </div>
    </div>
  );

  return (
    <div className="login-page" style={school ? { background: `linear-gradient(135deg, #1a1a2e, ${primary}55)` } : {}}>
      <div className="login-card">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {school?.logo
            ? <img src={school.logo} alt="logo" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }} />
            : <div style={{ width: 64, height: 64, borderRadius: 14, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <FaSchool size={28} color="#fff" />
              </div>
          }
          <h2 style={{ fontWeight: 700, fontSize: '1.3rem', color: '#111827' }}>
            {done ? 'Password Reset!' : 'Set New Password'}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 4 }}>{emailParam}</p>
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <FaCheckCircle size={52} color="#10b981" style={{ marginBottom: 16 }} />
            <p style={{ color: '#374151', lineHeight: 1.7, marginBottom: 24 }}>
              Your password has been reset successfully. You can now log in with your new password.
            </p>
            <button
              onClick={() => slug ? navigate(`/school/${slug}/login`) : navigate('/super-admin/login')}
              className="sms-btn sms-btn-primary"
              style={{ width: '100%', justifyContent: 'center', background: primary }}>
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label className="sms-label">New Password</label>
              <div style={{ position: 'relative' }}>
                <FaLock style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  className="sms-input"
                  style={{ paddingLeft: 38, paddingRight: 38 }}
                  type={showPass ? 'text' : 'password'}
                  placeholder="Min 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required minLength={8}
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  {showPass ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {/* Strength bar */}
              {password && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= str ? strengthColor[str] : '#e5e7eb', transition: 'background 0.2s' }} />
                    ))}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: strengthColor[str], fontWeight: 500 }}>{strengthLabel[str]}</div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label className="sms-label">Confirm New Password</label>
              <div style={{ position: 'relative' }}>
                <FaLock style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  className="sms-input"
                  style={{ paddingLeft: 38, borderColor: confirm && password !== confirm ? '#ef4444' : undefined }}
                  type={showPass ? 'text' : 'password'}
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                />
              </div>
              {confirm && password !== confirm && (
                <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 4 }}>Passwords do not match</div>
              )}
            </div>

            <button type="submit" disabled={loading} className="sms-btn sms-btn-primary"
              style={{ width: '100%', justifyContent: 'center', background: primary, marginBottom: 16 }}>
              {loading ? <Spinner size={18} color="#fff" /> : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
